import { useEffect, useRef } from 'react';
import { useEditor, EditorContent, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import {
  Bold as BoldIcon,
  Italic as ItalicIcon,
  Underline as UnderlineIcon,
  List as BulletIcon,
  ListOrdered as OrderedIcon,
  Heading2,
  Heading3,
  Link as LinkIcon,
  Link2Off,
  Undo2,
  Redo2,
} from 'lucide-react';
import { sanitizeRichText } from './sanitizeRichText';
import './RichTextEditor.css';

export interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  variant?: 'default' | 'embed';
  disabled?: boolean;
  ariaLabel?: string;
}

interface ToolbarButtonProps {
  onClick: () => void;
  active?: boolean;
  disabled?: boolean;
  title: string;
  children: React.ReactNode;
}

function ToolbarButton({ onClick, active, disabled, title, children }: ToolbarButtonProps) {
  return (
    <button
      type="button"
      className={`rte-toolbar__btn${active ? ' is-active' : ''}`}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      disabled={disabled}
      title={title}
      aria-label={title}
      aria-pressed={active}
    >
      {children}
    </button>
  );
}

function Toolbar({ editor }: { editor: Editor }) {
  const setLink = () => {
    const previous = editor.getAttributes('link').href as string | undefined;
    const url = window.prompt('URL du lien (https:// ou mailto:)', previous ?? 'https://');
    if (url === null) return;
    const trimmed = url.trim();
    if (trimmed === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    // Bloque les schémas dangereux dès la saisie
    if (!/^(https?:\/\/|mailto:)/i.test(trimmed)) {
      window.alert('Seuls les liens http, https et mailto sont acceptés.');
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: trimmed }).run();
  };

  return (
    <div className="rte-toolbar" role="toolbar" aria-label="Formatage du texte">
      <div className="rte-toolbar__group">
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBold().run()}
          active={editor.isActive('bold')}
          title="Gras"
        >
          <BoldIcon size={16} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleItalic().run()}
          active={editor.isActive('italic')}
          title="Italique"
        >
          <ItalicIcon size={16} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          active={editor.isActive('underline')}
          title="Souligné"
        >
          <UnderlineIcon size={16} />
        </ToolbarButton>
      </div>

      <div className="rte-toolbar__group">
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          active={editor.isActive('heading', { level: 2 })}
          title="Titre"
        >
          <Heading2 size={16} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          active={editor.isActive('heading', { level: 3 })}
          title="Sous-titre"
        >
          <Heading3 size={16} />
        </ToolbarButton>
      </div>

      <div className="rte-toolbar__group">
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          active={editor.isActive('bulletList')}
          title="Liste à puces"
        >
          <BulletIcon size={16} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          active={editor.isActive('orderedList')}
          title="Liste numérotée"
        >
          <OrderedIcon size={16} />
        </ToolbarButton>
      </div>

      <div className="rte-toolbar__group">
        <ToolbarButton onClick={setLink} active={editor.isActive('link')} title="Ajouter un lien">
          <LinkIcon size={16} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().unsetLink().run()}
          disabled={!editor.isActive('link')}
          title="Retirer le lien"
        >
          <Link2Off size={16} />
        </ToolbarButton>
      </div>

      <div className="rte-toolbar__group">
        <ToolbarButton
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().undo()}
          title="Annuler"
        >
          <Undo2 size={16} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().redo()}
          title="Rétablir"
        >
          <Redo2 size={16} />
        </ToolbarButton>
      </div>
    </div>
  );
}

export function RichTextEditor({
  value,
  onChange,
  placeholder,
  variant = 'default',
  disabled = false,
  ariaLabel,
}: RichTextEditorProps) {
  // Sanitise la valeur initiale (peut être du texte brut historique)
  const initialContentRef = useRef(sanitizeRichText(value));

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
        // On désactive les extensions non whitelistées côté sanitizer
        codeBlock: false,
        code: false,
        blockquote: false,
        horizontalRule: false,
        strike: false,
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        autolink: false,
        linkOnPaste: true,
        HTMLAttributes: {
          rel: 'noopener noreferrer nofollow',
          target: '_blank',
        },
      }),
    ],
    content: initialContentRef.current,
    editable: !disabled,
    editorProps: {
      attributes: {
        class: 'rte-content',
        'aria-label': ariaLabel ?? 'Éditeur de règles',
      },
    },
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  // Sync externe (ex. reset après Annuler)
  useEffect(() => {
    if (!editor) return;
    const sanitized = sanitizeRichText(value);
    if (sanitized !== editor.getHTML()) {
      editor.commands.setContent(sanitized || '<p></p>', false);
    }
  }, [value, editor]);

  useEffect(() => {
    if (!editor) return;
    editor.setEditable(!disabled);
  }, [disabled, editor]);

  if (!editor) {
    return null;
  }

  return (
    <div className={`rte-wrapper rte-wrapper--${variant}${disabled ? ' is-disabled' : ''}`}>
      <Toolbar editor={editor} />
      <div className="rte-editor" data-placeholder={placeholder ?? ''}>
        <EditorContent editor={editor} />
      </div>
    </div>
  );
}
